import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import * as React from "react";

import { cn } from "@/lib/utils";

export interface InputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "disabled" | "readOnly"
  > {
  loading?: boolean;
  readOnly?: boolean | string | React.ReactNode;
  disabled?: boolean | string | React.ReactNode;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      onBlur,
      loading,
      disabled = false,
      readOnly = false,
      ...props
    },
    ref,
  ) => {
    const isInvalid =
      props["aria-invalid"] === true || props["aria-invalid"] === "true";

    const inputElement = (
      <input
        type={type}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-0 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 read-only:cursor-not-allowed",
          Boolean(readOnly) && "cursor-not-allowed disabled:opacity-100",
          isInvalid &&
            "border-destructive bg-destructive/10 text-destructive focus:border-destructive focus:ring-destructive/50",
          className,
        )}
        ref={ref}
        disabled={Boolean(disabled) || Boolean(readOnly)}
        readOnly={Boolean(readOnly)}
        onBlur={(e) => {
          if (e.target.readOnly) {
            e.preventDefault();
            return;
          }
          onBlur?.(e);
        }}
        {...props}
      />
    );

    const message = React.useMemo(() => {
      if (disabled) {
        return disabled;
      }
      if (readOnly) {
        return readOnly;
      }
      return "";
    }, [disabled, readOnly]);

    if (loading) {
      return (
        <div className="animate-pulse rounded-md bg-primary/10 h-10 w-full" />
      );
    }

    if (
      typeof disabled === "string" ||
      typeof readOnly === "string" ||
      typeof readOnly === "object" ||
      typeof disabled === "object"
    ) {
      return (
        <HoverCard openDelay={1000} closeDelay={0}>
          <HoverCardTrigger asChild>{inputElement}</HoverCardTrigger>
          {Boolean(disabled || readOnly) && (
            <HoverCardContent
              align="start"
              sideOffset={-5}
              className="w-fit max-w-[500px] p-3"
            >
              {typeof message === "string" ? (
                <p className="text-sm">{message}</p>
              ) : (
                message
              )}
            </HoverCardContent>
          )}
        </HoverCard>
      );
    }

    return inputElement;
  },
);
Input.displayName = "Input";

export { Input };
